import {UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {
  getDocumentTitle,
  packReferenceUrl,
  SearchResult,
  unpackHmId,
  useRouteLinkHref,
  useSearch,
  useUniversalAppContext,
} from '@shm/shared'
import {ContentTypeFilter, SearchType} from '@shm/shared/client/.generated/entities/v1alpha/entities_pb'
import {useResource} from '@shm/shared/models/entity'
import {Fragment, PropsWithChildren, useEffect, useMemo, useRef, useState} from 'react'
import {Button} from './button'
import {ScrollArea} from './components/scroll-area'
import {Search} from './icons'
import {Spinner} from './spinner'
import {SizableText} from './text'
import {usePopoverState} from './use-popover-state'

import {IS_WEB} from '@shm/shared/constants'
import {useDebounce} from '@shm/shared/utils/use-debounce'
import {useIsomorphicLayoutEffect} from '@shm/shared/utils/use-isomorphic-layout-effect'
import {Input} from './components/input'
import {Popover, PopoverContent, PopoverTrigger} from './components/popover'
import {HMIcon} from './hm-icon'
import {Separator} from './separator'
import {Tooltip} from './tooltip'
import {cn} from './utils'

const SEARCH_DEBOUNCE_MS = 250

// Runs two parallel keyword searches and merges them:
// - Primary: scoped to the current site with iriFilter.
// - Secondary: global TITLE-only search, client-filtered to account-home
//   entries (no path) so foreign contributor profiles surface without
//   polluting results with unrelated docs from other sites.
// Keyword-only because the web server runs the embedder on CPU and HYBRID
// costs 5-8s; keyword is ~20ms per leg and the two run in parallel.
function useSiteSearch(searchValue: string, siteHomeId: UnpackedHypermediaId | null) {
  const query = searchValue.trim()
  const enabled = !!query
  const primary = useSearch(query, {
    enabled,
    iriFilter: siteHomeId?.uid ? `hm://${siteHomeId.uid}*` : undefined,
    includeBody: true,
    contextSize: Math.max(8, 48 - query.length),
    searchType: SearchType.SEARCH_KEYWORD,
    pageSize: 20,
  })
  const profiles = useSearch(query, {
    enabled,
    contentTypeFilter: [ContentTypeFilter.CONTENT_TYPE_TITLE],
    searchType: SearchType.SEARCH_KEYWORD,
    pageSize: 8,
  })
  const entities = useMemo(() => {
    const primaryEntities = primary.data?.entities ?? []
    const profileEntities = (profiles.data?.entities ?? []).filter((e) => !e.id.path || e.id.path.length === 0)
    const seen = new Set<string>()
    const out: typeof primaryEntities = []
    for (const e of [...profileEntities, ...primaryEntities]) {
      const key = packReferenceUrl(e.id)
      if (seen.has(key)) continue
      seen.add(key)
      out.push(e)
    }
    return out
  }, [primary.data, profiles.data])
  return {
    entities,
    isFetching: primary.isFetching || profiles.isFetching,
    isError: primary.isError || profiles.isError,
  }
}

function toSearchResults(entities: ReturnType<typeof useSiteSearch>['entities']): SearchResult[] {
  return entities.map((item) => {
    const title = item.title || item.id.uid
    return {
      id: item.id,
      key: packReferenceUrl(item.id),
      title,
      path: item.parentNames,
      icon: item.icon,
      onFocus: () => {},
      onMouseEnter: () => {},
      searchQuery: item.searchQuery,
      versionTime: item.versionTime || '',
    } as SearchResult
  })
}

function SearchStatusMessage({
  query,
  isLoading,
  isError,
  hasResults,
}: {
  query: string
  isLoading: boolean
  isError: boolean
  hasResults: boolean
}) {
  if (!query) {
    return <div className="text-muted-foreground p-4 text-center">Type to search documents</div>
  }
  if (isError) {
    return <div className="text-destructive p-4 text-center">Search failed</div>
  }
  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 p-4">
        <Spinner className="size-4" />
        <span>Searching…</span>
      </div>
    )
  }
  if (!hasResults) {
    return <div className="text-muted-foreground p-4 text-center">No results found</div>
  }
  return null
}

export function MobileSearch({
  siteHomeId,
  onSelect,
  onSearchActiveChange,
}: {
  siteHomeId: UnpackedHypermediaId | null
  onSelect: () => void
  onSearchActiveChange?: (isActive: boolean) => void
}) {
  const [searchValue, setSearchValue] = useState('')
  const trimmedSearchValue = searchValue.trim()
  const debouncedSearchValue = useDebounce(trimmedSearchValue, SEARCH_DEBOUNCE_MS)
  const isSearchActive = trimmedSearchValue.length > 0
  const isDebouncing = isSearchActive && debouncedSearchValue !== trimmedSearchValue
  const searchResults = useSiteSearch(debouncedSearchValue, siteHomeId)
  const isLoading = isDebouncing || searchResults.isFetching
  const resultEntities = isDebouncing ? [] : searchResults.entities
  const searchItems: SearchResult[] = toSearchResults(resultEntities)

  useEffect(() => {
    onSearchActiveChange?.(isSearchActive)
    return () => onSearchActiveChange?.(false)
  }, [isSearchActive, onSearchActiveChange])

  return (
    <div className="relative z-20 w-full gap-2 rounded-md p-2">
      <div className="relative">
        <Input
          className="w-full flex-1 pr-8"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value)
          }}
          placeholder="Search Documents"
        />
        {isLoading ? (
          <Spinner className="text-muted-foreground absolute top-1/2 right-2 size-4 -translate-y-1/2" />
        ) : null}
      </div>
      {isSearchActive ? (
        <div className="bg-background absolute inset-x-2 top-[calc(100%+8px)] z-20 max-h-[65dvh] overflow-hidden rounded-md border shadow-sm">
          <ScrollArea className="max-h-[65dvh]">
            <div className="py-2">
              {searchItems.length > 0 ? (
                searchItems.map((item: SearchResult) => {
                  return (
                    <Fragment key={item.key}>
                      <SearchResultItem
                        item={{
                          ...item,
                        }}
                        onSelect={onSelect}
                        siteHomeId={siteHomeId}
                        selected={false}
                      />
                    </Fragment>
                  )
                })
              ) : (
                <SearchStatusMessage
                  query={trimmedSearchValue}
                  isLoading={isLoading}
                  isError={searchResults.isError}
                  hasResults={searchItems.length > 0}
                />
              )}
            </div>
          </ScrollArea>
        </div>
      ) : null}
    </div>
  )
}

export function HeaderSearch({siteHomeId}: {siteHomeId: UnpackedHypermediaId | null}) {
  const popoverState = usePopoverState()
  const [searchValue, setSearchValue] = useState('')
  const trimmedSearchValue = searchValue.trim()
  const debouncedSearchValue = useDebounce(trimmedSearchValue, SEARCH_DEBOUNCE_MS)
  const isDebouncing = trimmedSearchValue.length > 0 && debouncedSearchValue !== trimmedSearchValue
  const searchResults = useSiteSearch(debouncedSearchValue, siteHomeId)
  const isLoading = isDebouncing || searchResults.isFetching
  const resultEntities = isDebouncing ? [] : searchResults.entities
  const [focusedIndex, setFocusedIndex] = useState(0)
  const universalAppContext = useUniversalAppContext()

  // Clear search when popover closes
  useEffect(() => {
    if (!popoverState.open) {
      setSearchValue('')
      setFocusedIndex(0)
    }
  }, [popoverState.open])

  // Listen for Command+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    if (!IS_WEB) return
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input, textarea, or contenteditable
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        popoverState.onOpenChange(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [popoverState])

  const searchItems: SearchResult[] = toSearchResults(resultEntities)

  useEffect(() => {
    if (focusedIndex >= searchItems.length) setFocusedIndex(0)
  }, [focusedIndex, searchItems])

  return (
    <div className="hidden flex-col sm:flex">
      <Popover
        {...popoverState}
        onOpenChange={(open) => {
          popoverState.onOpenChange(open)
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon">
            <Search className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="bottom" className="p-0">
          <div className="flex h-[calc(100vh-100px)] max-h-[600px] flex-col">
            <div className="relative flex items-center gap-2 self-stretch p-2">
              <Search className="absolute top-1/2 left-4 z-30 size-4 -translate-y-1/2" />
              {isLoading ? (
                <Spinner className="text-muted-foreground absolute top-1/2 right-4 z-30 size-4 -translate-y-1/2" />
              ) : null}
              <Input
                value={searchValue}
                className="h-8 flex-1 pr-8 pl-8"
                onChange={(e) => {
                  setSearchValue(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    popoverState.onOpenChange(false)
                  }

                  if (e.key === 'Enter') {
                    e.preventDefault()

                    if (!universalAppContext || isLoading) {
                      return
                    }

                    const selectedEntity = resultEntities[focusedIndex]

                    if (!selectedEntity) {
                      return
                    }

                    universalAppContext.openRoute?.({
                      key: 'document',
                      id: selectedEntity.id,
                    })

                    popoverState.onOpenChange(false)
                  }

                  if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    if (!searchItems.length) return
                    setFocusedIndex((prev) => (prev - 1 + searchItems.length) % searchItems.length)
                  }

                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    if (!searchItems.length) return
                    setFocusedIndex((prev) => (prev + 1) % searchItems.length)
                  }
                }}
              />
            </div>
            <div className="min-h-0 w-full max-w-2xl flex-1">
              <ScrollArea>
                <div className="flex flex-col">
                  {searchItems.length > 0 ? (
                    searchItems.map((item: SearchResult, index: number) => {
                      return (
                        <Fragment key={item.key}>
                          <div
                            ref={
                              focusedIndex === index
                                ? (el) => {
                                    if (el) {
                                      const container = el.closest('[data-radix-scroll-area-viewport]')
                                      if (container) {
                                        const containerRect = container.getBoundingClientRect()
                                        const elementRect = el.getBoundingClientRect()

                                        if (elementRect.bottom > containerRect.bottom) {
                                          container.scrollTop += elementRect.bottom - containerRect.bottom
                                        } else if (elementRect.top < containerRect.top) {
                                          container.scrollTop -= containerRect.top - elementRect.top
                                        }
                                      }
                                    }
                                  }
                                : undefined
                            }
                          >
                            <SearchResultItem
                              item={item}
                              siteHomeId={siteHomeId}
                              selected={focusedIndex === index}
                              // onSelect={() => {
                              //   popoverState.onOpenChange(false)
                              // }}
                            />
                          </div>
                          {index === searchItems.length - 1 ? undefined : <Separator />}
                        </Fragment>
                      )
                    })
                  ) : (
                    <SearchStatusMessage
                      query={trimmedSearchValue}
                      isLoading={isLoading}
                      isError={searchResults.isError}
                      hasResults={searchItems.length > 0}
                    />
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export function SearchResultItem({
  item,
  siteHomeId,
  selected = false,
  className,
  onSelect,
  ...props
}: {
  item: SearchResult
  siteHomeId?: UnpackedHypermediaId | null
  selected: boolean
  className?: string
  onSelect?: () => void
}) {
  const elm = useRef<HTMLDivElement>(null)
  const collapsedPath = useCollapsedPath(item.path ?? [], elm)
  const routeKey = item.key.split(':comments/')[0] || item.key
  const unpackedId = unpackHmId(routeKey)

  useIsomorphicLayoutEffect(() => {
    if (selected) {
      elm.current?.scrollIntoView({block: 'nearest'})
    }
  }, [selected])

  const navigateProps = useRouteLinkHref(routeKey)

  const selectProps = item.onSelect
    ? {
        onClick: () => {
          item.onSelect?.()
          onSelect?.()
        },
      }
    : {
        ...navigateProps,
        onClick: (e: any) => {
          onSelect?.()
          navigateProps?.onClick?.(e)
        },
      }

  return (
    <Button
      {...props}
      variant="ghost"
      {...selectProps}
      data-testid={`search-result-${item.title}`}
      className={cn(
        // dark:hover:bg-brand-12 overrides the ghost variant's dark:hover:bg-muted
        'hover:bg-brand-12 dark:hover:bg-brand-12 active:bg-brand-11 @container flex h-auto w-full items-start gap-3 rounded-none py-2',
        selected && 'bg-brand-12',
        className,
      )}
    >
      {/* Icon column (only takes space if present) */}
      {item.icon && (
        <div className="flex size-5 flex-none items-center justify-center">
          {unpackedId ? <HMIcon id={unpackedId} icon={item.icon} /> : null}
        </div>
      )}

      {/* Main (title/details) column */}
      <div className="flex min-w-0 flex-1 flex-col justify-start gap-1">
        <SizableText className="line-clamp-1 h-5 w-full truncate text-left font-sans font-medium">
          {!!item.path && unpackedId?.blockRef
            ? item.path[item.path?.length - 1]
            : highlightSearchMatch(item.title, item.searchQuery)}
        </SizableText>

        {unpackedId?.blockRef && (
          <SizableText size="xs" weight="light" className="line-clamp-1 flex-none text-left font-sans text-gray-400">
            ...{highlightSearchMatch(item.title, item.searchQuery)}...
          </SizableText>
        )}

        {!!item.path && (unpackedId?.latest || item.versionTime) && (
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            <div className="flex min-w-0 flex-1 items-center">
              {!!item.path && (
                <SizableText size="xs" weight="light" className="line-clamp-1 truncate font-sans text-gray-400">
                  {collapsedPath.join(' / ')}
                </SizableText>
              )}
            </div>

            {/* Type column */}
            <Tooltip content={item.versionTime || 'No timestamp available'}>
              <SizableText
                className="flex-none whitespace-nowrap text-gray-400"
                size="xs"
                weight="light"
                color={unpackedId?.latest ? 'success' : 'default'}
              >
                {unpackedId?.latest ? 'Latest Version' : item.versionTime ? 'Previous Version' : ''}
              </SizableText>
            </Tooltip>
          </div>
        )}
      </div>
    </Button>
  )
}

export type SearchResultItem = {
  key: string
  title: string
  subtitle?: string
  path: string[]
  id?: UnpackedHypermediaId
  onSelect: () => void
  onFocus: () => void
  onMouseEnter: () => void
}

export function RecentSearchResultItem({
  item,
  selected,
  siteHomeId,
  ...props
}: {
  item: SearchResultItem
  selected: boolean
  siteHomeId?: UnpackedHypermediaId | null
  onFocus?: (e: React.FocusEvent) => void
  onMouseEnter?: (e: React.MouseEvent) => void
  onMouseDown?: (e: React.MouseEvent) => void
}) {
  let path = normalizePath(item.path.slice(0, -1))
  const homeUnpacked = item.id ? unpackHmId(`hm://${item.id.uid}`) : null
  const homeEntity = useResource(homeUnpacked)
  if (item.id) {
    const doc = homeEntity.data?.type === 'document' ? homeEntity.data.document : undefined
    const homeTitle = getDocumentTitle(doc)

    if (homeTitle && homeTitle !== item.title) {
      path = [homeTitle, ...path]
    }
  }

  return (
    <SearchResultItem
      {...props}
      item={{
        ...item,
      }}
      selected={selected}
      siteHomeId={siteHomeId}
    />
  )
}

export function highlightSearchMatch(text: string, highlight: string = '') {
  if (!highlight) return text
  const parts = text.split(new RegExp(`(${escapeRegExp(highlight)})`, 'gi'))
  return (
    <>
      {parts.map((part, i) => {
        const isMatch = part.toLowerCase() === highlight.toLowerCase()
        return isMatch ? (
          <SizableText
            className="bg-brand-10 text-secondary-foreground inline-block rounded-md px-1 font-medium dark:text-white"
            key={i}
          >
            {part}
          </SizableText>
        ) : (
          part
        )
      })}
    </>
  )
}

export function SearchInput({
  children,
  inputProps,
  onArrowDown,
  onArrowUp,
  onEscape,
  onEnter,
  loading,
}: PropsWithChildren<{
  searchResults: Array<SearchResult>
  inputProps: {
    value: string
    onChangeText: (text: string) => void
    disabled: boolean
  }
  onEscape: () => void
  onArrowUp: () => void
  onArrowDown: () => void
  onEnter: () => void
  focusedIndex: number
  loading?: boolean
}>) {
  return (
    <div className="flex h-full w-full flex-col gap-2">
      <div className="relative flex items-center gap-2 rounded-md">
        <Search className="absolute top-1/2 left-2.5 z-3 size-4 -translate-y-1/2" />
        {loading ? (
          <Spinner className="absolute top-1/2 right-7 z-3 size-4 -translate-y-1/2 text-black/50 dark:text-white/50" />
        ) : null}
        <Input
          autoFocus={true}
          placeholder="Search Hypermedia documents"
          className="w-full px-1 pr-8 pl-8"
          {...inputProps}
          onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              onEscape?.()
            }

            if (e.nativeEvent.key === 'Enter') {
              e.preventDefault()
              onEnter?.()
            }

            if (e.nativeEvent.key === 'ArrowUp') {
              e.preventDefault()
              onArrowUp?.()
            }

            if (e.nativeEvent.key === 'ArrowDown') {
              e.preventDefault()
              onArrowDown?.()
            }
          }}
        />
      </div>
      <div className="max-h-[200px] min-h-0 flex-1 overflow-hidden">
        <ScrollArea className="h-full">{children}</ScrollArea>
      </div>
    </div>
  )
}
function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizePath(path: string[]): string[] {
  return path.map((segment) => {
    const [first, ...rest] = segment.split('-')
    // @ts-ignore
    return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ')
  })
}

export function useCollapsedPath(
  path: string[],
  containerRef: React.RefObject<HTMLElement>,
  fontSize = 12,
  maxWidth = 200, // fallback width if ref not ready
) {
  const [collapsedPath, setCollapsedPath] = useState<string[]>(path)

  useEffect(() => {
    if (!containerRef.current || path.length <= 3) {
      setCollapsedPath(path)
      return
    }

    const containerWidth = containerRef.current.offsetWidth || maxWidth
    const spacer = 10
    const charWidth = fontSize * 0.6 // approx width of each character

    // Estimate full breadcrumb width
    const fullWidth = path.reduce((acc, item) => acc + item.length * charWidth + spacer, 0)

    if (fullWidth <= containerWidth) {
      setCollapsedPath(path)
    } else {
      // @ts-ignore
      setCollapsedPath([path[0], '…', path[path.length - 1]])
    }
  }, [path, containerRef])

  return collapsedPath
}
